'use client';
import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Grid, Typography, useMediaQuery } from '@mui/material';
import imageUrlBuilder from '@sanity/image-url';
import SubpageBlob from './components/subpage-blob';
import { sanityClient } from '@/content/sanity/sanity-client/sanity-client';
import { config } from '@/config/environment';

const builder = imageUrlBuilder(sanityClient);
function urlFor(source: any) {
  return builder.image(source).url();
}

const MainDiv = styled(Grid)`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: #231727;
  padding: 10.2% 12.5% 7.6% 12.5%;
  @media (max-width: 768px) {
    padding: 98px 20px 20px 20px;
    height: auto;
  }
`;
const Div = styled(Grid)`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  :nth-of-type(2) {
    margin-top: 90px;
    @media (max-width: 768px) {
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      gap: 50px;
      margin-top: 40px;
      margin-bottom: 100px;
    }
  }
`;
const TitleDiv = styled(Grid)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  @media (max-width: 768px) {
    width: 100%;
    padding: 0;
    margin-bottom: 32px;
  }
`;
const Title = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-size: 50px;
  line-height: 109.3%;
  color: #fdf3de;
  text-align: center;
  margin: 0;
  @media (max-width: 768px) {
    width: 100%;
    text-align: start;
    color: #ffffff;
  }
`;

const Text = styled(Typography)`
  font-family: var(--font-work-sans), sans-serif;
  font-size: 18px;
  line-height: 159.3%;
  color: #ffffff;
`;

interface AboutUsDocument {
  _id: string;
  title: string;
  description: string;
  sections: {
    title: string;
    image: any;
    link: string;
  }[];
}

const About = () => {
  const [aboutData, setAboutData] = useState<AboutUsDocument | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const isMobile = useMediaQuery('(max-width:768px)');
  const archiveMode = config.platformMode === 'archive';

  // For default props in SubpageBlob mapping if 4 sections are expected.
  const justifyArray = ['start', 'center', 'center', 'flex-end'];
  const widthArrayDesktop = ['202px', '174px', '200px', '235px'];
  const widthArrayMobile = ['133px', '122px', '140px', '138px'];

  useEffect(() => {
    const fetchAboutData = async () => {
      const data = await sanityClient.fetch(
        `*[_type=="aboutUs"][0]{ _id, title, description, sections[]{ title, image, link } }`,
      );
      setAboutData(data);
      setLoading(false);
    };

    fetchAboutData();
  }, []);

  if (loading || !aboutData) {
    return <MainDiv>Loading...</MainDiv>;
  }

  const visibleTitle = archiveMode ? 'About Curiko' : aboutData.title;

  return (
    <MainDiv container>
      <Div container>
        <TitleDiv item md={6} xs={12}>
          <Title>{visibleTitle}</Title>
        </TitleDiv>
        <TitleDiv item md={6} xs={12}>
          {archiveMode ? (
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Text sx={{ mb: 2 }}>
                Curiko operated from 2021–2026 as a community where people with and without
                disabilities came together through shared experiences. More than a platform, it
                remains a living experiment in belonging, reciprocity, and collective learning.
              </Text>
              <Text>
                This archival site shares the values, ideas, and people that shaped Curiko—from its
                origins and theory of action to the team and community that brought it to life.
              </Text>
            </div>
          ) : (
            <Text>{aboutData.description}</Text>
          )}
        </TitleDiv>
      </Div>
      <Div container>
        {aboutData.sections.map((section, index) => (
          <SubpageBlob
            key={index}
            justify={justifyArray[index] || 'center'}
            width={widthArrayDesktop[index] || '150px'}
            vector={urlFor(section.image)}
            subpageName={section.title}
            link={section.link}
          />
        ))}
      </Div>
    </MainDiv>
  );
};

export default About;
